import React from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";

const LAST_UPDATED = "22 July 2026";

const DocShell = ({ title, kicker, children, testId, seoPath, seoDescription }) => (
  <div data-testid={testId} className="pt-[76px]">
    <Seo title={title} path={seoPath} description={seoDescription} />
    <section className="mx-auto max-w-[820px] px-5 md:px-8 pt-14 md:pt-20 pb-16">
      <p className="kicker mb-5">{kicker}</p>
      <h1 className="headline text-4xl md:text-6xl uppercase text-ink leading-[1.05]">{title}</h1>
      <p className="mt-6 font-mono text-xs uppercase tracking-widest text-foreground/50">Last updated · {LAST_UPDATED}</p>
    </section>
    <section className="mx-auto max-w-[720px] px-5 md:px-8 pb-24 legal-body">
      {children}
      <hr className="my-14 border-border" />
      <p className="text-sm text-foreground/60">
        See also:{" "}
        <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>{" · "}
        <Link to="/terms" className="text-primary hover:underline">Terms of Use</Link>{" · "}
        <Link to="/cookies" className="text-primary hover:underline">Cookie Notice</Link>
      </p>
    </section>
  </div>
);

export const Privacy = () => (
  <DocShell
    title="Privacy Policy"
    kicker="Legal"
    testId="privacy-page"
    seoPath="/privacy"
    seoDescription="How Which Media collects, uses and safeguards your personal information."
  >
    <p>
      Which Media (&ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;our&rdquo;) is the group behind WhichSchoolAdvisor.com,
      SchoolsCompared.com, Top Schools Awards, EdinformaPlus, the Education Intelligence Group and WhichUniversityAdvisor.com.
      This policy explains what personal data we collect on the corporate site at <strong>which-media.com</strong>, why we
      collect it, and the rights you have over it.
    </p>

    <h2>1. Data we collect</h2>
    <ul>
      <li><strong>Contact form:</strong> your name, email, company (optional), the brand or team you selected, and the content of your message.</li>
      <li><strong>Job applications:</strong> the fields above plus any CV you attach (PDF, DOC or DOCX). Stored securely and only visible to authorised HR staff.</li>
      <li><strong>Newsletter:</strong> your email address and the brands you chose to hear about.</li>
      <li><strong>Technical logs:</strong> for security and abuse prevention we briefly record your IP address, device type and the URL you requested. We use this to rate-limit our forms — nothing more.</li>
      <li><strong>Analytics (optional):</strong> if analytics are enabled we may record aggregate visit statistics. See our <Link to="/cookies">Cookie Notice</Link>.</li>
    </ul>

    <h2>2. Why we collect it</h2>
    <ul>
      <li>To respond to your enquiry or application.</li>
      <li>To deliver the newsletter you opted in to.</li>
      <li>To defend our services from abuse and spam.</li>
      <li>To understand, in aggregate, which content is useful — so we can make the site better.</li>
    </ul>

    <h2>3. How long we keep it</h2>
    <ul>
      <li>Contact enquiries: up to <strong>36 months</strong> after last contact.</li>
      <li>Job applications: up to <strong>24 months</strong> after the last relevant vacancy, unless you ask for earlier deletion.</li>
      <li>Newsletter subscriptions: for as long as you remain subscribed. Unsubscribe removes your record within 30 days.</li>
    </ul>

    <h2>4. Who sees it</h2>
    <p>
      Personal data is accessible only to authorised Which Media employees and contractors who need it to do their jobs. We
      never sell your data, and we do not share it with unrelated third parties for marketing. We may share necessary
      technical logs with our infrastructure providers (hosting, email delivery, error tracking) under strict data-processing
      terms.
    </p>

    <h2>5. Your rights</h2>
    <p>
      Under GDPR, UAE data protection law and other applicable regimes you can ask us to:
    </p>
    <ul>
      <li>Confirm what personal data we hold about you and give you a copy.</li>
      <li>Correct any inaccurate information.</li>
      <li>Delete your data (right to be forgotten), unless we are legally required to keep it.</li>
      <li>Restrict or object to specific processing.</li>
      <li>Withdraw consent for the newsletter or analytics at any time.</li>
    </ul>
    <p>
      To exercise any of these rights, email <a href="mailto:privacy@which-media.com" className="text-primary hover:underline">privacy@which-media.com</a>{" "}
      from the same address you used with us. We reply within 30 days.
    </p>

    <h2>6. Security</h2>
    <p>
      All traffic to the site is served over HTTPS. Passwords are hashed with bcrypt; admin sessions use short-lived JSON
      Web Tokens. Uploaded files (CVs) are stored on private disk and only released via authenticated admin download.
    </p>

    <h2>7. Changes</h2>
    <p>
      We may update this policy from time to time. The &ldquo;Last updated&rdquo; date above shows when. Material changes will be
      surfaced on the site.
    </p>
  </DocShell>
);

export const Terms = () => (
  <DocShell
    title="Terms of Use"
    kicker="Legal"
    testId="terms-page"
    seoPath="/terms"
    seoDescription="The terms under which Which Media makes this corporate website available."
  >
    <p>
      These terms govern your use of <strong>which-media.com</strong>. By using the site you agree to them. If you don&rsquo;t,
      please don&rsquo;t use the site.
    </p>

    <h2>1. Who we are</h2>
    <p>
      Which Media is a media and intelligence group headquartered in Dubai, UAE, with editorial and commercial operations in
      the UAE and the United Kingdom.
    </p>

    <h2>2. Editorial content</h2>
    <p>
      Content on which-media.com and its associated brands is offered in good faith for information purposes. It is not
      financial, legal or educational advice. You should verify important decisions independently.
    </p>

    <h2>3. Intellectual property</h2>
    <p>
      All text, imagery, brand marks, videos and design elements on the site are the property of Which Media or its
      licensors. You may share links freely. You may not republish, resell or otherwise commercially exploit our content
      without prior written permission.
    </p>

    <h2>4. Acceptable use</h2>
    <p>You agree not to:</p>
    <ul>
      <li>Attempt to access parts of the site you are not authorised to access.</li>
      <li>Interfere with the normal operation of the site (including automated scraping at abusive rates).</li>
      <li>Upload malware, offensive content, or content you do not have the rights to.</li>
      <li>Impersonate any other person or organisation via our forms.</li>
    </ul>

    <h2>5. Third-party links</h2>
    <p>
      The site may link to third-party websites. We&rsquo;re not responsible for their content or policies.
    </p>

    <h2>6. Limitation of liability</h2>
    <p>
      To the maximum extent permitted by law, Which Media is not liable for any indirect or consequential loss arising from
      use of the site. Nothing in these terms limits or excludes liability for fraud, death or personal injury caused by our
      negligence.
    </p>

    <h2>7. Governing law</h2>
    <p>
      These terms are governed by the laws of the United Arab Emirates. Any dispute will be handled by the competent courts
      of Dubai, without prejudice to any mandatory consumer protections in your local jurisdiction.
    </p>

    <h2>8. Contact</h2>
    <p>
      Questions about these terms: <a href="mailto:legal@which-media.com" className="text-primary hover:underline">legal@which-media.com</a>.
    </p>
  </DocShell>
);

export const Cookies = () => (
  <DocShell
    title="Cookie Notice"
    kicker="Legal"
    testId="cookies-page"
    seoPath="/cookies"
    seoDescription="What cookies and similar technologies Which Media uses, and how you can control them."
  >
    <p>
      This notice explains the small pieces of data we store in your browser and why. If you accepted or declined our cookie
      banner earlier, you can change your mind at any time by clearing your browser storage for this site.
    </p>

    <h2>1. Strictly necessary</h2>
    <p>
      A small number of browser-storage entries are always used — they are not cookies in the tracking sense, they simply
      make the site work:
    </p>
    <ul>
      <li><code>wm_token</code> — your session token if you sign in to the CMS. Removed on sign-out.</li>
      <li><code>wm_cookie_consent</code> — remembers your choice on the cookie banner so we don&rsquo;t nag you every visit.</li>
    </ul>

    <h2>2. Analytics</h2>
    <p>
      Where enabled, we use one of two analytics tools:
    </p>
    <ul>
      <li>
        <strong>Plausible</strong> — a privacy-focused product that <em>does not use cookies</em> and does not track individual
        users. Aggregate visit stats only. No consent required.
      </li>
      <li>
        <strong>Google Analytics 4</strong> — if enabled, sets a small number of first-party cookies (typically <code>_ga</code>{" "}
        and <code>_ga_XXXX</code>) with a 2-year expiry to measure aggregate usage. IP addresses are anonymised. We only load
        GA4 <em>after</em> you accept the cookie banner.
      </li>
    </ul>

    <h2>3. Third parties</h2>
    <p>
      Embedded content from third parties (for example a Vimeo video on a brand page) may set their own cookies once
      loaded. See their respective privacy notices for details.
    </p>

    <h2>4. Managing your choice</h2>
    <p>
      To reset your consent choice, clear the <code>wm_cookie_consent</code> item from your browser&rsquo;s local storage — the
      banner will reappear on your next visit.
    </p>
  </DocShell>
);

export default Privacy;
